import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import { useFormContext } from '@jarvis.oddle/core/components/Form'
import {
  buttonStyles,
  margin,
  marginSizes
} from '@jarvis.oddle/core/theme/default'
import { isObjectEmpty } from 'src/utils/object'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import React from 'react'
import styled, { ThemeContext } from 'styled-components'
import { paymentMethods, paymentTypes } from 'src/utils/payment'
import { buttonStyle } from '@jarvis.oddle/core/components/Button'
import Loading from '@jarvis.oddle/core/components/Loading'

const StyledPayButton = styled(Div)`
  width: 100%;
  margin: ${margin[marginSizes.none]} ${margin[marginSizes.medium]};
  ${buttonStyle}
  ${breakpoint('tablet')`
    ${(props) => ({ width: props.width })}
  `}
`

interface PaymentMethods extends Oms.V4.PaymentMethodData {
  parsedId: string
}

interface SubmitButtonElementProps {
  selectedPaymentMethod: PaymentMethods
  buttonText: string
  stripePaymentRequestObject: {
    canMakePayment: () => Record<string, boolean>
    on: (x, y) => void
    show: () => void
  }
  isStripeReady: boolean
  mobileScaleFullWidth?: boolean
  isEnabledCustomerSaveCreditCardOnStripe?: boolean
  selectedSavedCreditCard?: Oms.V4.SelectedSavedCreditCard
}

const SubmitButtonElement = ({
  selectedPaymentMethod,
  buttonText,
  isStripeReady,
  mobileScaleFullWidth = false,
  isEnabledCustomerSaveCreditCardOnStripe = false,
  selectedSavedCreditCard
}: SubmitButtonElementProps): JSX.Element => {
  const { buttonStyle, buttonShape, buttonTextColor, buttonColor } =
    React.useContext(ThemeContext)
  const form = useFormContext()

  const { type: paymentType } = selectedPaymentMethod || {}

  // disable the checkout button when user select stripe as the payment method and stripe
  // element has not been loaded into the iframe.
  let isDisabled = false
  if (paymentType === paymentTypes.stripe) {
    isDisabled = !isStripeReady
  } else if (paymentType === paymentTypes.stripeExpressAccount) {
    if (
      isEnabledCustomerSaveCreditCardOnStripe &&
      !isObjectEmpty(selectedSavedCreditCard) &&
      selectedPaymentMethod.paymentMethod !== paymentMethods.FPX &&
      selectedPaymentMethod.stripeRequestPaymentButtonType !==
        paymentMethods.APPLE_PAY
    ) {
      isDisabled = false
    } else {
      isDisabled = !isStripeReady
    }
  }

  const onButtonClick = React.useCallback(async () => {
    const { validateForm, handleSubmit } = form
    // Validation
    const formErrors = await validateForm()
    if (!isObjectEmpty(formErrors)) return
    // Register on token callback
    handleSubmit()
  }, [form])

  return selectedPaymentMethod === null ? null : (
    <Flex flexDirection='row' justifyContent='center'>
      <StyledPayButton
        width='300px'
        disabled={isDisabled || form.isSubmitting}
        mobileScaleFullWidth={mobileScaleFullWidth}
        onClick={isDisabled ? null : onButtonClick}
        buttonStyle={buttonStyle}
        buttonShape={buttonShape}
        buttonTextColor={buttonTextColor}
        buttonColor={buttonColor}
      >
        {form.isSubmitting ? (
          <Loading
            height='22px'
            width='22px'
            color={
              buttonStyle === buttonStyles.outline
                ? buttonColor
                : buttonTextColor
            }
          />
        ) : (
          buttonText
        )}
      </StyledPayButton>
    </Flex>
  )
}

export default SubmitButtonElement
